import type { CSSProperties } from "react";
import type { ThemeTokens } from "@course-authoring/shared-types";

export interface DesignLibraryStyles {
  layout: CSSProperties;
  nav: CSSProperties;
  navHeader: CSSProperties;
  navList: CSSProperties;
  navLink: CSSProperties;
  main: CSSProperties;
  pageHeader: CSSProperties;
  pageTitle: CSSProperties;
  pageSubtitle: CSSProperties;
  statusBanner: CSSProperties;
  statusBannerRow: CSSProperties;
  statusPill: (status: "active" | "proposed" | "deprecated" | "needs-review") => CSSProperties;
  section: CSSProperties;
  sectionHeader: CSSProperties;
  sectionTitle: CSSProperties;
  sectionDescription: CSSProperties;
  subsectionTitle: CSSProperties;
  tokenGrid: CSSProperties;
  tokenCard: CSSProperties;
  swatch: (background: string) => CSSProperties;
  tokenPath: CSSProperties;
  tokenValue: CSSProperties;
  tokenDescription: CSSProperties;
  tokenMetaRow: CSSProperties;
  table: CSSProperties;
  tableHeader: CSSProperties;
  tableCell: CSSProperties;
  stub: CSSProperties;
  blockShowcase: CSSProperties;
  blockShowcaseHeader: CSSProperties;
  blockShowcaseBody: CSSProperties;
  blockTokenList: CSSProperties;
  notesBlock: CSSProperties;
}

const STATUS_COLORS: Record<
  "active" | "proposed" | "deprecated" | "needs-review",
  { bg: string; fg: string; border: string }
> = {
  active: { bg: "#DBF1E5", fg: "#1BA13F", border: "#1BA13F" },
  proposed: { bg: "#FFF7E6", fg: "#B26A00", border: "#F59E0B" },
  deprecated: { bg: "#F0DEDE", fg: "#B91C1C", border: "#DC2626" },
  "needs-review": { bg: "#E1EBFF", fg: "#1F4ED8", border: "#5B8EFF" },
};

export function createDesignLibraryStyles(tokens: ThemeTokens): DesignLibraryStyles {
  const fontFamily = tokens.typography.fontFamily;

  return {
    layout: {
      display: "grid",
      gridTemplateColumns: "240px 1fr",
      minHeight: "100vh",
      width: "100%",
      backgroundColor: tokens.backgrounds.page,
      fontFamily,
      color: tokens.text.primary,
    },
    nav: {
      position: "sticky",
      top: 0,
      alignSelf: "start",
      height: "100vh",
      overflowY: "auto",
      borderRight: `1px solid ${tokens.borders.default}`,
      padding: `${tokens.spacing.xl} ${tokens.spacing.lg}`,
      backgroundColor: tokens.backgrounds.surface,
    },
    navHeader: {
      fontSize: tokens.typography.sizes.sm,
      fontWeight: tokens.typography.weights.semibold,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: tokens.text.muted,
      marginBottom: tokens.spacing.md,
    },
    navList: {
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacing.xs,
      listStyle: "none",
      padding: 0,
      margin: 0,
    },
    navLink: {
      display: "block",
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.sm,
      fontSize: tokens.typography.sizes.sm,
      color: tokens.text.primary,
      textDecoration: "none",
      cursor: "pointer",
    },
    main: {
      padding: `${tokens.spacing["2xl"]} ${tokens.spacing["3xl"]}`,
      maxWidth: "1200px",
      width: "100%",
      boxSizing: "border-box",
    },
    pageHeader: {
      marginBottom: tokens.spacing["2xl"],
    },
    pageTitle: {
      fontSize: tokens.typography.sizes.h1,
      fontWeight: tokens.typography.weights.bold,
      lineHeight: tokens.typography.lineHeights.tight,
      margin: 0,
      marginBottom: tokens.spacing.sm,
    },
    pageSubtitle: {
      fontSize: tokens.typography.sizes.md,
      color: tokens.text.muted,
      margin: 0,
    },
    statusBanner: {
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacing.sm,
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      marginBottom: tokens.spacing["2xl"],
    },
    statusBannerRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: tokens.spacing.sm,
      alignItems: "center",
    },
    statusPill: (status) => {
      const palette = STATUS_COLORS[status];
      return {
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.spacing.xs,
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        borderRadius: tokens.radius.full,
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: tokens.typography.sizes.xs,
        fontWeight: tokens.typography.weights.semibold,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      };
    },
    section: {
      paddingTop: tokens.spacing["2xl"],
      paddingBottom: tokens.spacing["2xl"],
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    sectionHeader: {
      marginBottom: tokens.spacing.lg,
    },
    sectionTitle: {
      fontSize: tokens.typography.sizes.h2,
      fontWeight: tokens.typography.weights.bold,
      margin: 0,
      marginBottom: tokens.spacing.xs,
    },
    sectionDescription: {
      fontSize: tokens.typography.sizes.base,
      color: tokens.text.muted,
      margin: 0,
    },
    subsectionTitle: {
      fontSize: tokens.typography.sizes.lg,
      fontWeight: tokens.typography.weights.semibold,
      margin: 0,
      marginTop: tokens.spacing.xl,
      marginBottom: tokens.spacing.md,
    },
    tokenGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      gap: tokens.spacing.md,
    },
    tokenCard: {
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacing.xs,
      padding: tokens.spacing.md,
      backgroundColor: tokens.backgrounds.card,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
    },
    swatch: (background: string) => ({
      width: "100%",
      height: "64px",
      borderRadius: tokens.radius.sm,
      backgroundColor: background,
      border: `1px solid ${tokens.borders.default}`,
    }),
    tokenPath: {
      fontSize: tokens.typography.sizes.sm,
      fontWeight: tokens.typography.weights.semibold,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      color: tokens.text.primary,
      wordBreak: "break-all",
    },
    tokenValue: {
      fontSize: tokens.typography.sizes.xs,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      color: tokens.text.muted,
    },
    tokenDescription: {
      fontSize: tokens.typography.sizes.xs,
      color: tokens.text.muted,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    tokenMetaRow: {
      display: "flex",
      alignItems: "center",
      gap: tokens.spacing.xs,
      flexWrap: "wrap",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: tokens.typography.sizes.sm,
    },
    tableHeader: {
      textAlign: "left",
      padding: tokens.spacing.sm,
      borderBottom: `1px solid ${tokens.borders.default}`,
      fontWeight: tokens.typography.weights.semibold,
      color: tokens.text.muted,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      fontSize: tokens.typography.sizes.xs,
    },
    tableCell: {
      padding: tokens.spacing.sm,
      borderBottom: `1px solid ${tokens.borders.default}`,
      verticalAlign: "top",
    },
    stub: {
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      color: tokens.text.muted,
      fontSize: tokens.typography.sizes.sm,
    },
    blockShowcase: {
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.card,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      marginBottom: tokens.spacing.lg,
    },
    blockShowcaseHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: tokens.spacing.md,
      flexWrap: "wrap",
    },
    blockShowcaseBody: {
      fontSize: tokens.typography.sizes.sm,
      color: tokens.text.muted,
    },
    blockTokenList: {
      display: "flex",
      flexWrap: "wrap",
      gap: tokens.spacing.xs,
    },
    notesBlock: {
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      fontSize: tokens.typography.sizes.sm,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
  };
}
